// Utility functions for image handling and HEIC conversion

export function isHEICFile(file: File): boolean {
  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();
  
  return fileName.endsWith('.heic') || 
         fileName.endsWith('.heif') || 
         fileType === 'image/heic' || 
         fileType === 'image/heif';
}

export async function convertHeicToJpeg(file: File): Promise<File> {

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {

      const img = new Image();
      
      img.onload = () => {

        try {
          // Create canvas with image dimensions
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {

            reject(new Error('Canvas context not available'));
            return;
          }
          
          canvas.width = img.width;
          canvas.height = img.height;
          
          // Draw image to canvas
          ctx.drawImage(img, 0, 0);

          // Convert to JPEG blob
          canvas.toBlob((blob) => {
            if (!blob) {

              reject(new Error('Failed to convert image to JPEG'));
              return;
            }

            // Create new File from blob with JPEG extension
            const convertedFileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
            const convertedFile = new File([blob], convertedFileName, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });

            resolve(convertedFile);
          }, 'image/jpeg', 0.8); // 0.8 quality for good balance of size/quality
          
        } catch (error) {

          reject(error);
        }
      };
      
      img.onerror = (error) => {

        reject(new Error('Failed to load image for conversion'));
      };
      
      img.src = e.target?.result as string;
    };
    
    reader.onerror = (error) => {

      reject(new Error('Failed to read HEIC file'));
    };
    
    reader.readAsDataURL(file);
  });
}

export async function processFileForUpload(file: File): Promise<File> {

  // Check if file is HEIC and needs conversion
  if (isHEICFile(file)) {

    try {
      const convertedFile = await convertHeicToJpeg(file);

      return convertedFile;
    } catch (error) {

      throw new Error(`Failed to convert HEIC file "${file.name}". Please use JPEG or PNG format.`);
    }
  }

  return file; // Return original file if no conversion needed
}

/**
 * Compress an image file to reduce its size while maintaining quality
 * @param file - Image file to compress
 * @param maxSizeMB - Target maximum size in MB (default: 10MB)
 * @param quality - Compression quality 0-1 (default: 0.85)
 * @returns Compressed image file
 */
export async function compressImage(
  file: File,
  maxSizeMB: number = 10,
  quality: number = 0.85
): Promise<File> {
  // Convert HEIC/HEIF to JPEG first so the canvas can read it
  let inputFile = file;
  if (isHEICFile(file)) {
    inputFile = await convertHeicToJpeg(file);
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('Canvas context not available'));
            return;
          }

          // Calculate new dimensions (max 4096px on longest side)
          let width = img.width;
          let height = img.height;
          const maxDimension = 4096;

          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;

          // Draw image to canvas with high quality
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);

          // Try different quality levels until size is acceptable
          const tryCompress = (currentQuality: number) => {
            canvas.toBlob((blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }

              const targetSize = maxSizeMB * 1024 * 1024;

              // If still too large and quality can be reduced, try again
              if (blob.size > targetSize && currentQuality > 0.5) {
                console.log(`[Compress] Size ${(blob.size / 1024 / 1024).toFixed(2)}MB > ${maxSizeMB}MB, reducing quality to ${(currentQuality - 0.1).toFixed(2)}`);
                tryCompress(currentQuality - 0.1);
                return;
              }

              // Create compressed file
              const compressedFileName = inputFile.name.replace(/\.[^.]+$/, '.jpg');
              const compressedFile = new File([blob], compressedFileName, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });

              console.log(`[Compress] Original: ${(file.size / 1024 / 1024).toFixed(2)}MB -> Compressed: ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
              resolve(compressedFile);
            }, 'image/jpeg', currentQuality);
          };

          tryCompress(quality);

        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image for compression'));
      };

      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read image file'));
    };

    reader.readAsDataURL(inputFile);
  });
}

/**
 * Check if a file is an image that can be compressed
 */
export function isCompressibleImage(file: File): boolean {
  const compressibleTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  return compressibleTypes.includes(file.type.toLowerCase()) || isHEICFile(file);
}

export function validateFileForUpload(file: File): { valid: boolean; error?: string } {
  // Check file size (10MB limit)
  if (file.size > 10 * 1024 * 1024) {
    return {
      valid: false,
      error: `File "${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum size is 10MB.`
    };
  }

  // Check file type
  const allowedTypes = [
    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
    'application/pdf',
    'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    // Archive formats
    'application/zip', 'application/x-zip-compressed',
    'application/gzip', 'application/x-gzip',
    'application/x-tar', 'application/x-compressed-tar',
    'application/x-7z-compressed'
  ];

  const allowedExtensions = [
    '.jpg', '.jpeg', '.png', '.gif', '.webp',
    '.pdf',
    '.doc', '.docx',
    '.txt',
    '.xls', '.xlsx',
    '.heic', '.heif', // Allow HEIC for conversion
    // Archive extensions
    '.zip', '.gz', '.tar', '.tar.gz', '.tgz', '.7z'
  ];

  const fileName = file.name.toLowerCase();
  const hasValidType = allowedTypes.includes(file.type) || isHEICFile(file);
  const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

  if (!hasValidType && !hasValidExtension) {
    return {
      valid: false,
      error: `File type not supported: "${file.name}". Please use images, PDFs, documents, or archives.`
    };
  }

  return { valid: true };
}